/* eslint-disable array-callback-return */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getUser, updateStore, getConfiguration } from "src/api/sp";

export const fetchStoreDetails = createAsyncThunk(
  "user/storeDetails",
  ({ email, idToken }: { email: string; idToken: string }) =>
    getUser({ email, idToken })
);

export const updateStoreDetails = createAsyncThunk(
  "user/updateStoreDetails",
  (action: any) => updateStore(action)
);

export const fetchConfiguration = createAsyncThunk(
  "user/fetchConfiguration",
  () => getConfiguration()
);

export const selectStore = (stores: any) =>
  stores.find((store: any) => store.isDefault);

export const storeName = (stores: any) => {
  const user = stores.filter((userDetail: any) => {
    if (userDetail.isDefault === true) {
      return userDetail;
    }
  });
  return user[0].city;
};

export interface authUserObject {
  email: string;
  firstName: string;
  id: string;
  isActive: string;
  lastName: string;
  roles: [];
  snoozedItems: [];
  stores: Array<storeDetail>;
  userName: string;
}

export interface storeData {
  data: authUserObject | any;
  status: "idle" | "pending";
  error: string;
  storeID: string;
  storeName: string;
  storeState: string;
  storeStateShort: string;
  appConfiguration?: appConfiguration;
}

export interface storeDetail {
  storeID: string;
  storeName: string;
  isDefault: boolean;
}

export interface appConfiguration {
  [key: string]: any;
}

const initialState: storeData = {
  data: {},
  status: "idle",
  error: "",
  storeID: "",
  storeName: "",
  storeState: "",
  storeStateShort: "",
  appConfiguration: null
};

const authUser = createSlice({
  name: "Header",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchStoreDetails.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchStoreDetails.fulfilled, (state, { payload }) => {
        state.data = payload.data;
        const storeData = selectStore(payload.data.stores);
        const storeName = storeData?.storeName;
        const storeState = storeData?.storeState;
        const storeId = storeData?.storeId;

        localStorage.setItem("storeName", storeName);
        localStorage.setItem("storeId", storeId);

        state.storeID = storeId;
        state.storeName = storeName;
        state.storeState = storeData?.city;
        state.storeStateShort = storeState;
        state.status = "idle";
      })
      .addCase(fetchStoreDetails.rejected, (state) => {
        state.status = "idle";
      })
      .addCase(updateStoreDetails.pending, (state, action) => {
        state.status = "pending";
        const args = action.meta.arg;
        state.storeID = args.storeId;
        state.storeName = args.storeName;
      })
      .addCase(updateStoreDetails.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(updateStoreDetails.rejected, (state) => {
        state.status = "idle";
        state.storeID = localStorage.getItem("storeId");
        state.storeName = localStorage.getItem("storeName");
      })
      .addCase(fetchConfiguration.fulfilled, (state, { payload }) => {
        state.appConfiguration = payload.data;
      });
  }
});

export const getStore = ({
  authUser
}: {
  authUser: storeData;
}): { [key: string]: any } | undefined => authUser?.data;

export const getUserName = (state: any) =>
  `${state.auth?.data?.firstName} ${state.auth?.data?.lastName}`;

export const getCurrentStoreDetails = ({ auth }: { auth: storeData }) =>
  auth?.data?.stores?.find((store: any) => store?.id === auth?.storeID);

export const getCurrentStoreId = ({ auth }: { auth: storeData }) =>
  auth?.storeID;

export const getStoreName = ({ auth }: { auth: storeData }) => auth.storeName;

export const selectAuth = ({ auth }: { auth: storeData }) => auth;

export const selectAppConfiguration = ({ auth }: { auth: storeData }) =>
  auth?.appConfiguration;

export const selectGoAppsLinks = ({ auth }: { auth: storeData }) =>
  auth?.appConfiguration?.goAppsLinks;

export const selectIcareLinks = ({ auth }: { auth: storeData }) =>
  auth?.appConfiguration?.iCareLinks;

export default authUser.reducer;
